import { Fragment, useEffect, useRef, useState } from 'react';

import { GripVertical, Pencil, Trash2 } from 'lucide-react';

import { useAuth } from '@chainlit/react-client';

function linkifyText(text: string) {
  const urlRegex = /https?:\/\/[^\s]+/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    let url = match[0];
    const trailing = url.match(/[.,;:!?)"']+$/);
    if (trailing) url = url.slice(0, url.length - trailing[0].length);
    parts.push(
      <a
        key={match.index}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-primary underline underline-offset-2 hover:text-primary/80 break-all"
      >
        {url}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length === 0 ? text : <>{parts}</>;
}


interface Task {
  id: number;
  title: string;
  notes: string | null;
  done: boolean;
  subtasks: Task[];
}

function DeleteConfirmDialog({
  taskTitle,
  onConfirm,
  onCancel
}: {
  taskTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60">
          <span className="text-sm font-semibold">Delete task?</span>
        </div>
        <div className="p-4 flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Delete{' '}
            <span className="text-foreground font-medium">"{taskTitle}"</span>?
            This cannot be undone.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted/40 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-3 py-1.5 text-sm rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskRow({
  task,
  indent = false,
  onToggle,
  onEdit,
  onDelete,
  onAddSubtask,
  dropIndicator,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}: {
  task: Task;
  indent?: boolean;
  onToggle: (id: number) => void;
  onEdit: (task: Task) => void;
  onDelete: (id: number) => void;
  onAddSubtask?: (id: number) => void;
  dropIndicator?: 'before' | 'after' | null;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <li
      className="group relative"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {dropIndicator === 'before' && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary/80 z-20 pointer-events-none" />
      )}
      {dropIndicator === 'after' && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/80 z-20 pointer-events-none" />
      )}
      <div className="flex items-start hover:bg-muted/40 transition-colors relative">
        <span className="absolute left-0 top-0 bottom-0 flex items-center pl-1 opacity-0 group-hover:opacity-100 cursor-grab text-muted-foreground/30 z-10">
          <GripVertical size={12} />
        </span>
        <label className="flex items-start gap-2.5 px-3 py-1.5 cursor-pointer flex-1 min-w-0">
          {indent && (
            <span className="w-3 shrink-0 text-border select-none">└</span>
          )}
          <input
            type="checkbox"
            checked={task.done}
            onChange={() => onToggle(task.id)}
            className="mt-[3px] shrink-0 accent-primary cursor-pointer"
          />
          <span className="flex flex-col min-w-0">
            <span
              className={
                'text-sm leading-snug' +
                (task.done
                  ? ' line-through text-muted-foreground/60'
                  : indent
                  ? ' text-muted-foreground'
                  : ' text-foreground')
              }
            >
              {linkifyText(task.title)}
            </span>
            {task.notes && !task.done && (
              <span className="text-xs text-muted-foreground/70 leading-snug mt-0.5">
                {linkifyText(task.notes)}
              </span>
            )}
          </span>
        </label>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 shrink-0 mt-1.5 mr-2">
          <button
            onClick={() => onEdit(task)}
            title="Edit task"
            className="p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted/50"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={() => setConfirming(true)}
            title="Delete task"
            className="p-1 rounded text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 size={13} />
          </button>
          {!indent && onAddSubtask && (
            <button
              onClick={() => onAddSubtask(task.id)}
              title="Add subtask"
              className="w-5 h-5 flex items-center justify-center rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 text-base leading-none"
            >
              +
            </button>
          )}
        </div>
      </div>
      {confirming && (
        <DeleteConfirmDialog
          taskTitle={task.title}
          onConfirm={() => {
            setConfirming(false);
            onDelete(task.id);
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </li>
  );
}

function EditTaskModal({
  task,
  onClose,
  onSaved
}: {
  task: Task;
  onClose: () => void;
  onSaved: (id: number, title: string, notes: string | null) => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [notes, setNotes] = useState(task.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const titleRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/tasks/edit', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: task.id, title: title.trim(), notes: notes.trim() })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to save');
        setSaving(false);
        return;
      }
      onSaved(task.id, title.trim(), notes.trim() || null);
      onClose();
    } catch {
      setError('Network error');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60">
          <span className="text-sm font-semibold">Edit task</span>
        </div>
        <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Title</label>
            <textarea
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              rows={2}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary/50 resize-y leading-snug"
              required
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">
              Notes <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Optional notes"
              rows={4}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary/50 resize-y leading-snug"
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddTaskModal({
  tasks,
  initialParentId,
  onClose,
  onAdded
}: {
  tasks: Task[];
  initialParentId?: number | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [parentId, setParentId] = useState<number | null>(initialParentId ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const titleRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/tasks/add', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          notes: notes.trim() || null,
          parent_id: parentId
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to add task');
        setSaving(false);
        return;
      }
      onAdded();
      onClose();
    } catch {
      setError('Network error');
      setSaving(false);
    }
  };

  const topLevel = tasks.filter((t) => !t.done);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60">
          <span className="text-sm font-semibold">Add task</span>
        </div>
        <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Title</label>
            <textarea
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Task title"
              rows={2}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary/50 resize-y leading-snug"
              required
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">
              Notes <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Optional notes"
              rows={4}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary/50 resize-y leading-snug"
            />
          </div>
          {topLevel.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">
                Parent task{' '}
                <span className="text-muted-foreground/50">(optional)</span>
              </label>
              <select
                value={parentId ?? ''}
                onChange={(e) =>
                  setParentId(e.target.value ? Number(e.target.value) : null)
                }
                className="w-full rounded-lg border border-border bg-muted/30 px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/50"
              >
                <option value="">None (top-level task)</option>
                {topLevel.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function dropPosition(e: React.DragEvent): 'before' | 'after' {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  return e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
}

export default function HomeTaskList() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [addSubtaskParentId, setAddSubtaskParentId] = useState<number | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const dragRef = useRef<{ id: number; isSubtask: boolean; parentId: number | null } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: number; position: 'before' | 'after' } | null>(null);

  const email = user?.identifier;

  const loadTasks = () => {
    fetch('/api/tasks', { method: 'POST', credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: Task[]) => setTasks(data))
      .catch(() => {});
  };

  useEffect(() => {
    if (!email) return;
    loadTasks();
  }, [email]);

  const handleEditSaved = (id: number, title: string, notes: string | null) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === id) return { ...t, title, notes };
        return { ...t, subtasks: t.subtasks.map((s) => s.id === id ? { ...s, title, notes } : s) };
      })
    );
  };

  const handleDelete = (id: number) => {
    setTasks((prev) =>
      prev
        .filter((t) => t.id !== id)
        .map((t) => ({ ...t, subtasks: t.subtasks.filter((s) => s.id !== id) }))
    );
    fetch('/api/tasks/delete', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    }).catch(() => loadTasks());
  };

  const handleToggle = (id: number) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === id) return { ...t, done: !t.done };
        return {
          ...t,
          subtasks: t.subtasks.map((s) =>
            s.id === id ? { ...s, done: !s.done } : s
          )
        };
      })
    );
    fetch('/api/tasks/toggle', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    }).catch(() => loadTasks());
  };

  const handleDragStart = (id: number, isSubtask: boolean, parentId: number | null) => {
    dragRef.current = { id, isSubtask, parentId };
  };

  const handleDragEnd = () => {
    dragRef.current = null;
    setDropTarget(null);
  };

  const handleDragOver = (e: React.DragEvent, id: number, isSubtask: boolean, parentId: number | null) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag || drag.id === id) { setDropTarget(null); return; }
    if (drag.isSubtask !== isSubtask || drag.parentId !== parentId) { setDropTarget(null); return; }
    setDropTarget({ id, position: dropPosition(e) });
  };

  const handleDrop = (e: React.DragEvent, id: number, isSubtask: boolean, parentId: number | null) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag || drag.id === id) return;
    if (drag.isSubtask !== isSubtask || drag.parentId !== parentId) return;

    const fromId = drag.id;
    const position = dropPosition(e);
    dragRef.current = null;
    setDropTarget(null);

    if (!isSubtask) {
      const fromIdx = tasks.findIndex((t) => t.id === fromId);
      if (fromIdx < 0) return;
      const moved = tasks[fromIdx];
      const rest = tasks.filter((_, i) => i !== fromIdx);
      const targetIdx = rest.findIndex((t) => t.id === id);
      if (targetIdx < 0) return;
      const insertIdx = position === 'after' ? targetIdx + 1 : targetIdx;
      const newItems = [...rest.slice(0, insertIdx), moved, ...rest.slice(insertIdx)];
      setTasks(newItems);
      fetch('/api/tasks/reorder', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordered_ids: newItems.map((t) => t.id) })
      }).catch(() => loadTasks());
    } else {
      const newTasks = tasks.map((t) => {
        if (t.id !== parentId) return t;
        const fromIdx = t.subtasks.findIndex((s) => s.id === fromId);
        if (fromIdx < 0) return t;
        const moved = t.subtasks[fromIdx];
        const rest = t.subtasks.filter((_, i) => i !== fromIdx);
        const targetIdx = rest.findIndex((s) => s.id === id);
        if (targetIdx < 0) return t;
        const insertIdx = position === 'after' ? targetIdx + 1 : targetIdx;
        const newSubs = [...rest.slice(0, insertIdx), moved, ...rest.slice(insertIdx)];
        return { ...t, subtasks: newSubs };
      });
      setTasks(newTasks);
      const parent = newTasks.find((t) => t.id === parentId);
      if (parent) {
        fetch('/api/tasks/reorder', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ordered_ids: parent.subtasks.map((s) => s.id) })
        }).catch(() => loadTasks());
      }
    }
  };

  if (!email) return null;

  return (
    <>
      <div className="w-full rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Tasks
          </span>
          <button
            onClick={() => { setAddSubtaskParentId(null); setShowModal(true); }}
            title="Add task"
            className="w-5 h-5 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-base leading-none"
          >
            +
          </button>
        </div>
        {!tasks.length && (
          <p className="px-3 py-3 text-xs text-muted-foreground/60">
            Tap <span className="font-semibold">+</span> to add a task.
          </p>
        )}
        <ul className="divide-y divide-border/40">
          {tasks.map((task) => (
            <Fragment key={task.id}>
              <TaskRow
                task={task}
                onToggle={handleToggle}
                onEdit={setEditingTask}
                onDelete={handleDelete}
                onAddSubtask={(id) => { setAddSubtaskParentId(id); setShowModal(true); }}
                dropIndicator={dropTarget?.id === task.id ? dropTarget.position : null}
                onDragStart={() => handleDragStart(task.id, false, null)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, task.id, false, null)}
                onDrop={(e) => handleDrop(e, task.id, false, null)}
              />
              {task.subtasks.map((sub) => (
                <TaskRow
                  key={sub.id}
                  task={sub}
                  indent
                  onToggle={handleToggle}
                  onEdit={setEditingTask}
                  onDelete={handleDelete}
                  dropIndicator={dropTarget?.id === sub.id ? dropTarget.position : null}
                  onDragStart={() => handleDragStart(sub.id, true, task.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, sub.id, true, task.id)}
                  onDrop={(e) => handleDrop(e, sub.id, true, task.id)}
                />
              ))}
            </Fragment>
          ))}
        </ul>
      </div>
      {showModal && (
        <AddTaskModal
          tasks={tasks}
          initialParentId={addSubtaskParentId}
          onClose={() => { setShowModal(false); setAddSubtaskParentId(null); }}
          onAdded={loadTasks}
        />
      )}
      {editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSaved={(id, title, notes) => {
            handleEditSaved(id, title, notes);
            setEditingTask(null);
          }}
        />
      )}
    </>
  );
}
